import {
  Injectable,
  inject,
  signal,
  computed
} from '@angular/core';

import {
  Observable,
  BehaviorSubject,
  firstValueFrom
} from 'rxjs';

// ======================================================
// SERVICES
// ======================================================

import { HttpService } from '../http/http.service';
import { CookieService } from './cookie.service';

// ======================================================
// MODELS
// ======================================================

import {
  Email,
  ResetPasswordPayload,
  SigninPayload,
  SignupPayload,
  UserInfo
} from '../models/auth.model';

// ======================================================
// ENUMS
// ======================================================

import { Name } from '../enums/name.enum';

// ======================================================
// API ENDPOINTS
// ======================================================

import {
  ADMIN_SIGNIN_URL,
  SIGNUP_URL,
  RESET_PASSWORD_URL,
  VERIFY_2FA_URL,
  VERIFY_EMAIL_URL,
  ACCEPT_TERM_CONDITION,
  GET_SUBSCRIPTION,
  CONTACT_TO_SALES,
  RE_ACCEPT_TERM_AND_CONDITIONS
} from '../constants/apiEndpoints.constants';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // ======================================================
  // DI
  // ======================================================

  private readonly http =
    inject(HttpService);

  private readonly cookieSrv =
    inject(CookieService);

  // ======================================================
  // STATE
  // ======================================================

readonly isLoggedIn = signal(false);

  readonly user = signal<UserInfo | null>(null);

  private subscriptionEndDate:
    Date | null = null;

  // ======================================================
  // CONSTRUCTOR
  // ======================================================

  constructor() {
    this.setIsLoggedIn();
  }

  // ======================================================
  // COMPUTED
  // ======================================================

  // readonly isLoggedIn = computed(() => {
  //   return this.isLoggedIn$.value;
  // });

  // ======================================================
  // AUTH STATE
  // ======================================================

  setIsLoggedIn(): void {

    const token =
      this.cookieSrv.get(
        Name.AUTH_TOKEN
      );

    this.isLoggedIn.set(!!token);
  }

  signOut(): void {

    this.cookieSrv.delete(
      Name.AUTH_TOKEN
    );

    localStorage.clear();

    this.user.set(null);

    this.isLoggedIn.set(false);
  }

  // ======================================================
  // API CALLS
  // ======================================================

  signIn(
    payload: SigninPayload
  ): Observable<any> {

    return this.http.post(
      ADMIN_SIGNIN_URL,
      payload
    );
  }

  signUp(
    payload: SignupPayload
  ): Observable<any> {

    return this.http.post(
      SIGNUP_URL,
      payload
    );
  }

  verifySignupEmail(
    payload: Email
  ): Observable<any> {

    return this.http.post(
      VERIFY_EMAIL_URL,
      payload
    );
  }

  resetPassword(
    payload: ResetPasswordPayload
  ): Observable<any> {

    return this.http.post(
      RESET_PASSWORD_URL,
      payload
    );
  }

  sendOtp(
    payload: Email
  ): Observable<any> {

    return this.http.post(
      VERIFY_EMAIL_URL,
      payload
    );
  }

  verify2FA(
    payload: {
      otp: number | null;
    }
  ): Observable<any> {

    return this.http.post(
      VERIFY_2FA_URL,
      payload
    );
  }

  getSignupOTP(
    payload: {
      user_token: string;
    }
  ): Observable<any> {

    return this.http.post(
      ACCEPT_TERM_CONDITION,
      payload
    );
  }

  getSubscription():
    Observable<any> {

    return this.http.get(
      GET_SUBSCRIPTION
    );
  }

  contactToSales(
    payload: {
      subject: string;
      message: string;
    }
  ): Observable<any> {

    return this.http.post(
      CONTACT_TO_SALES,
      payload
    );
  }

  reAcceptPolicyTermAndCondition():
    Observable<any> {

    return this.http.post(
      RE_ACCEPT_TERM_AND_CONDITIONS,
      null
    );
  }

  contactToSalesToUnblockAskISP(
  url: string,
  payload: unknown
): Observable<any> {

  return this.http.post(
    url,
    payload
  );
}

  // ======================================================
  // LOCAL STORAGE
  // ======================================================

  setUserInfoToLocalstorage(
    key: string,
    value: unknown
  ): void {

    localStorage.setItem(
      key,
      JSON.stringify(value)
    );
  }

updateUserInfoToLocalstorage(
  key: keyof UserInfo,
  value: unknown
): void {

  const user =
    this.getUserInformation<UserInfo>();

  if (!user) return;

  user[key] = value;

  localStorage.setItem(
    Name.USER_INFO,
    JSON.stringify(user)
  );
}

  getUserInfoFromLocalstorage<T>(
    key: string
  ): T | null {

    const data =
      localStorage.getItem(key);

    return data
      ? JSON.parse(data)
      : null;
  }

getUserInformation<T>() {

  return this.getUserInfoFromLocalstorage<T>(
    Name.USER_INFO
  );
}

  // ======================================================
  // SUBSCRIPTION
  // ======================================================

  setSubscriptionEnd(
    date: string | Date
  ): void {

    this.subscriptionEndDate =
      date
        ? new Date(date)
        : null;
  }

  getSubscriptionEndDate():
    Date | null {

    if (this.subscriptionEndDate) {
      return this.subscriptionEndDate;
    }

    const data: any =
      this.getUserInfoFromLocalstorage(
        Name.USER_INFO
      );

    this.subscriptionEndDate =
      data?.subscriptionEndTs
        ? new Date(
            data.subscriptionEndTs
          )
        : null;

    return this.subscriptionEndDate;
  }

  isSubscriptionStarted():
    boolean {

    return !!this.getSubscriptionEndDate();
  }

  isSubscriptionActive():
    boolean {

    const endDate =
      this.getSubscriptionEndDate();

    if (!endDate) {
      return false;
    }

    return (
      endDate.getTime() >
      Date.now()
    );
  }

  getRemainingDays():
    number {

    const endDate =
      this.getSubscriptionEndDate();

    if (!endDate) {
      return 0;
    }

    const diff =
      endDate.getTime() -
      Date.now();

    return diff > 0
      ? Math.ceil(
          diff / 86_400_000
        )
      : 0;
  }

  isOver30DaysPast():
    boolean {

    const endDate =
      this.getSubscriptionEndDate();

    if (!endDate) {
      return false;
    }

    const diff =
      Date.now() -
      endDate.getTime();

    return diff >
      30 * 86_400_000;
  }

  get daysAfterExpiry():
    number {

    const endDate =
      this.getSubscriptionEndDate();

    if (!endDate) {
      return 0;
    }

    const diff =
      Date.now() -
      endDate.getTime();

    if (diff <= 0) {
      return 0;
    }

    return Math.floor(
      diff / 86_400_000
    );
  }
}